import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

interface LiveUser {
  device_id: number;
  user_id: number;
  name: string | null;
  phone_number: string | null;
  platform: string | null;
  app_version: string | null;
  last_seen_at: string | null;
  online: boolean;
  status: string | null;
  is_under_maintenance: boolean;
  // How many active sessions this user has in total — the list shows only the newest.
  session_count: number;
}

interface UserDevice {
  id: number;
  device_name: string | null;
  device_type: string | null;
  app_version: string | null;
  is_active: boolean;
  is_verified: boolean;
  last_seen_at: string | null;
  ip_address: string | null;
  created_at: string | null;
}

interface VersionStat { app_version: string; android: number; ios: number; total: number; }
interface LiveStats {
  total_users: number;
  online_count: number;
  // 'instance' = the count only covers the server that answered.
  presence_scope?: 'cluster' | 'instance';
  by_platform: Record<string, number>;
  by_version: VersionStat[];
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '-');
const AUTO_REFRESH_MS = 30_000;
const EXPORT_PAGE = 100;   // backend caps `limit` at 100
const EXPORT_MAX_PAGES = 50; // 5,000 users — beyond that we tell the admin it was cut

const PlatformBadge: React.FC<{ p: string | null }> = ({ p }) => {
  const v = (p || '').toLowerCase();
  const cls = v === 'ios' ? 'bg-gray-800 text-white' : v === 'android' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{p || 'unknown'}</span>;
};

const LiveUsers: React.FC = () => {
  const [rows, setRows] = useState<LiveUser[]>([]);
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [platform, setPlatform] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Expanded row: the user's other sessions, fetched on demand.
  const [expanded, setExpanded] = useState<number | null>(null);
  const [devices, setDevices] = useState<UserDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Debounce the search box; reset to page 1 when the term changes.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const filterParams = useCallback(() => {
    const params: any = {};
    if (platform) params.platform = platform;
    if (appVersion) params.app_version = appVersion;
    if (onlineOnly) params.online_only = 'true';
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    return params;
  }, [platform, appVersion, onlineOnly, debouncedSearch]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const res: any = await api.get('/users/live', { params: { ...filterParams(), page, limit: 20 } });
      const payload = res.data || {};
      setRows(payload.data || []);
      setTotalPages(payload.pagination?.total_pages || 1);
      setTotal(payload.pagination?.total ?? 0);
      setOnlineCount(payload.online_count || 0);
    } catch (e) {
      if (!opts?.silent) toast.error('Failed to load live users');
    } finally { if (!opts?.silent) setLoading(false); }
  }, [filterParams, page]);

  // Cards are counted with the same filters minus the version one — they ARE the
  // version breakdown, so filtering them by version would leave a single bar.
  const loadStats = useCallback(async () => {
    try {
      const params = filterParams();
      delete params.app_version;
      const res: any = await api.get('/users/live/stats', { params });
      setStats(res.data || null);
    } catch (e) { /* cards are a nice-to-have; the table still works */ }
  }, [filterParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Auto-refresh: silent reloads so the table doesn't flash a loader every 30s.
  const refreshRef = useRef(load);
  refreshRef.current = load;
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => refreshRef.current({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const toggleExpand = async (u: LiveUser) => {
    if (expanded === u.user_id) { setExpanded(null); return; }
    setExpanded(u.user_id);
    setDevices([]);
    setDevicesLoading(true);
    try {
      const res: any = await api.get(`/users/${u.user_id}/devices`);
      setDevices(res.data || []);
    } catch (e) {
      toast.error('Failed to load this user\'s sessions');
    } finally { setDevicesLoading(false); }
  };

  const toggleMaintenance = async (u: LiveUser) => {
    const next = !u.is_under_maintenance;
    if (!window.confirm(`${next ? 'Enable' : 'Disable'} maintenance for ${u.name || '#' + u.user_id}? They will ${next ? 'see the maintenance screen' : 'use the app normally'}.`)) return;
    setSavingId(u.user_id);
    try {
      await api.patch(`/users/${u.user_id}/maintenance`, { enabled: next });
      toast.success(`Maintenance ${next ? 'enabled' : 'disabled'}`);
      setRows((prev) => prev.map((r) => (r.user_id === u.user_id ? { ...r, is_under_maintenance: next } : r)));
    } catch (e) {
      toast.error('Failed to update maintenance');
    } finally { setSavingId(null); }
  };

  // Force logout — clears the session token(s), so the user's next request is rejected
  // and the app sends them back to login. `deviceRowId` ends one session only.
  const forceLogout = async (u: LiveUser, deviceRowId?: number) => {
    const what = deviceRowId ? 'this session' : `all ${u.session_count} session${u.session_count === 1 ? '' : 's'}`;
    if (!window.confirm(`Log out ${what} for ${u.name || '#' + u.user_id}? They will have to sign in again.`)) return;
    setSavingId(u.user_id);
    try {
      const res: any = await api.post(`/users/${u.user_id}/logout`, deviceRowId ? { device_row_id: deviceRowId } : {});
      toast.success(res.message || 'Logged out');
      setExpanded(null); // the session list just changed — reopen it to see the new state
      load();
      loadStats();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to log the user out');
    } finally { setSavingId(null); }
  };

  // CSV of the CURRENT filters, paged through the API (cap announced, never silent).
  const exportCsv = async () => {
    setExporting(true);
    try {
      const all: LiveUser[] = [];
      let p = 1;
      let pages = 1;
      do {
        const res: any = await api.get('/users/live', { params: { ...filterParams(), page: p, limit: EXPORT_PAGE } });
        const payload = res.data || {};
        all.push(...(payload.data || []));
        pages = payload.pagination?.total_pages || 1;
        p += 1;
      } while (p <= pages && p <= EXPORT_MAX_PAGES);

      const header = ['user_id', 'name', 'phone', 'platform', 'app_version', 'online', 'last_login', 'sessions'];
      const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [
        header.join(','),
        ...all.map((u) => [u.user_id, u.name ?? '', u.phone_number ?? '', u.platform ?? '', u.app_version ?? '',
          u.online ? 'online' : 'offline', u.last_seen_at ?? '', u.session_count].map(esc).join(',')),
      ].join('\n');

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `live-users-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      if (pages > EXPORT_MAX_PAGES) toast(`Exported the first ${all.length} users (list is larger — narrow the filters for the rest).`);
      else toast.success(`Exported ${all.length} users`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const versionOptions = stats?.by_version ?? [];

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Live Users</h1>
          <p className="text-sm text-gray-500">
            One row per user — their newest session. <span className="text-green-600 font-medium">{onlineCount} online now</span>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or phone…"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-56"
          />
          <select value={platform} onChange={(e) => { setPage(1); setPlatform(e.target.value); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All platforms</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
          <select value={appVersion} onChange={(e) => { setPage(1); setAppVersion(e.target.value); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm" title="Filter by the app version users are on">
            <option value="">All versions</option>
            {versionOptions.map((v) => (
              <option key={v.app_version} value={v.app_version}>{v.app_version} ({v.total})</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input type="checkbox" checked={onlineOnly} onChange={(e) => { setPage(1); setOnlineOnly(e.target.checked); }} />
            Online only
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none" title={`Reload every ${AUTO_REFRESH_MS / 1000}s`}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button onClick={() => { load(); loadStats(); }} className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Refresh</button>
          <button onClick={exportCsv} disabled={exporting}
            className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60">
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Summary cards — counted the same way the table is built, so they always agree. */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">Users with a session</div>
            <div className="text-2xl font-semibold text-gray-900">{stats.total_users.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">Online now</div>
            <div className="text-2xl font-semibold text-green-600">{stats.online_count.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1"
              title={stats.presence_scope === 'cluster'
                ? 'Counted across all API instances'
                : 'Only this API instance can be seen — with more than one running, the real number is higher'}>
              socket-connected{stats.presence_scope === 'instance' ? ' · this server only' : ''}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">Android / iOS</div>
            <div className="text-2xl font-semibold text-gray-900">
              {(stats.by_platform.android ?? 0).toLocaleString()}
              <span className="text-gray-300"> / </span>
              {(stats.by_platform.ios ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">Latest version</div>
            <div className="text-2xl font-semibold text-gray-900">
              {versionOptions[0] ? versionOptions[0].total.toLocaleString() : '—'}
            </div>
            <div className="text-xs text-gray-400 mt-1">{versionOptions[0]?.app_version ?? 'no data'}</div>
          </div>
        </div>
      )}

      {/* Version breakdown — click a version to filter the table to it. */}
      {versionOptions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
          <div className="text-sm font-medium text-gray-800 mb-3">Users by app version</div>
          <div className="flex flex-wrap gap-2">
            {versionOptions.map((v) => {
              const active = appVersion === v.app_version;
              return (
                <button key={v.app_version}
                  onClick={() => { setPage(1); setAppVersion(active ? '' : v.app_version); }}
                  title={`Android ${v.android} · iOS ${v.ios}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'border-gray-300 text-gray-700 hover:border-primary-500'}`}>
                  {v.app_version} <span className={active ? 'text-white/80' : 'text-gray-400'}>{v.total.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Counted once per user, from their newest session. Use "Notifications → Broadcast" to message a specific version.
          </p>
        </div>
      )}

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3 w-8"></th>
                  <th className="text-left font-medium px-4 py-3">User</th>
                  <th className="text-left font-medium px-4 py-3">Phone</th>
                  <th className="text-left font-medium px-4 py-3">Platform</th>
                  <th className="text-left font-medium px-4 py-3">App version</th>
                  <th className="text-left font-medium px-4 py-3">Online</th>
                  <th className="text-left font-medium px-4 py-3">Last login</th>
                  <th className="text-left font-medium px-4 py-3">Maintenance</th>
                  <th className="text-left font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No sessions</td></tr>
                ) : rows.map((u) => (
                  <React.Fragment key={u.user_id}>
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <button onClick={() => toggleExpand(u)} title="Show this user's other sessions"
                          className="w-6 h-6 rounded hover:bg-gray-100 text-gray-500 leading-none">
                          {expanded === u.user_id ? '▾' : '▸'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {u.name || <span className="text-gray-400">—</span>} <span className="text-gray-400">#{u.user_id}</span>
                        {u.session_count > 1 && (
                          <span className="ml-2 text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{u.session_count} sessions</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{u.phone_number || '-'}</td>
                      <td className="px-4 py-3"><PlatformBadge p={u.platform} /></td>
                      <td className="px-4 py-3 text-gray-600">{u.app_version || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-3">
                        {u.online
                          ? <span className="inline-flex items-center gap-1.5 text-green-600 text-xs font-medium"><span className="w-2 h-2 rounded-full bg-green-500" />online</span>
                          : <span className="text-gray-400 text-xs">offline</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{fmt(u.last_seen_at)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleMaintenance(u)} disabled={savingId === u.user_id}
                          className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${u.is_under_maintenance ? 'bg-amber-500' : 'bg-gray-300'}`}
                          title={u.is_under_maintenance ? 'This user sees the maintenance screen' : 'User uses the app normally'}>
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${u.is_under_maintenance ? 'translate-x-5' : ''}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => forceLogout(u)} disabled={savingId === u.user_id}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                          Log out
                        </button>
                      </td>
                    </tr>
                    {expanded === u.user_id && (
                      <tr className="bg-gray-50 border-t border-gray-100">
                        <td colSpan={9} className="px-4 py-3">
                          {devicesLoading ? (
                            <div className="text-sm text-gray-500 py-2">Loading sessions…</div>
                          ) : devices.length === 0 ? (
                            <div className="text-sm text-gray-400 py-2">No sessions found</div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="text-gray-500">
                                <tr>
                                  <th className="text-left font-medium py-1.5">Device</th>
                                  <th className="text-left font-medium py-1.5">Platform</th>
                                  <th className="text-left font-medium py-1.5">App version</th>
                                  <th className="text-left font-medium py-1.5">Last login</th>
                                  <th className="text-left font-medium py-1.5">Created</th>
                                  <th className="text-left font-medium py-1.5">State</th>
                                  <th className="text-left font-medium py-1.5"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {devices.map((d) => (
                                  <tr key={d.id} className="border-t border-gray-200">
                                    <td className="py-1.5 text-gray-700">{d.device_name || '—'}</td>
                                    <td className="py-1.5"><PlatformBadge p={d.device_type} /></td>
                                    <td className="py-1.5 text-gray-600">{d.app_version || '—'}</td>
                                    <td className="py-1.5 text-gray-600">{fmt(d.last_seen_at)}</td>
                                    <td className="py-1.5 text-gray-500">{fmt(d.created_at)}</td>
                                    <td className="py-1.5">
                                      {d.is_active
                                        ? <span className="text-green-600">active</span>
                                        : <span className="text-gray-400">logged out</span>}
                                    </td>
                                    <td className="py-1.5 text-right">
                                      {d.is_active && (
                                        <button onClick={() => forceLogout(u, d.id)} disabled={savingId === u.user_id}
                                          className="text-xs px-2 py-1 rounded border border-gray-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                                          Log out
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            A new session row is created on every login, so old app versions stay listed here.
                          </p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Prev</button>
            <span className="text-gray-500">Page {page} of {totalPages}{total ? ` · ${total.toLocaleString()} users` : ''}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};
export default LiveUsers;
